/**
 * 选项字典的**唯一前端缓存**（v0.61）。
 *
 * ★ 为什么是 store 而不是 Context：
 *    消费点在 FeatureTable / SipItemsTable / PartMasterPage 这些深处，用 Context
 *    要么改每个组件的签名、要么穿透五六层 props。这与 v0.47 的 `store/auth.ts`
 *    是同一个决定 —— 一份状态、各处 `useOptionValues(kind)` 各自订阅。
 *
 * ★ 为什么还要 `FALLBACK`（出厂兜底）：
 *    「空下拉比旧值更糟」—— 接口没回来、库还没迁移、网络抖一下，都会让下拉
 *    变成空的，那样用户**连选都选不了**（受控 `<select>` 不能手输）。
 *    所以拉不到时一律用 `lib/annot.ts` 等出厂常量顶上，并记 `degraded=true`
 *    让界面能诚实地说明"现在用的是内置清单"。
 *
 * ⚠️ 绝不在这里再存一份"某字段当前值"之类的东西 —— 那是业务数据，有它自己的
 *    owner。本 store 只回答"有哪些可选值"。
 */
import { create } from 'zustand';
import { getOptions } from '../api/client';
import type { OptionItem, OptionsResponse } from '../types';
import { METHOD_OPTS } from '../lib/annot';
import { SIP_KIND_PRESET } from '../lib/sipKind';

/** 八类清单的 kind。与后端 `db/models.py` 的 `OPTION_KINDS` **逐字一致**。 */
export type OptionKind =
  | 'method'          // 检测工具（量具）—— 气泡图页 + 指导书页 + 测量报告 F 列
  | 'freq'            // 检验频率 —— 指导书页
  | 'customer'        // 客户 —— 零件主数据
  | 'project_type'    // 项目类型 —— 零件主数据
  | 'mold_type'       // 模具类型 —— 零件主数据
  | 'cavity_count'    // 模穴 —— 零件主数据
  | 'press_tonnage'   // 冲床吨位 —— 零件主数据
  | 'sip_kind';       // 作业指导书「类别」列 —— SIP 明细表（v0.68）

export const OPTION_KINDS: OptionKind[] = [
  'method', 'freq', 'customer', 'project_type', 'mold_type', 'cavity_count',
  'press_tonnage', 'sip_kind',
];

/**
 * 出厂兜底（后端也有一份 `option_service.DEFAULT_OPTIONS`）。
 *
 * ⚠️ 两份不是"同一语义两处实现"，而是**两层防线**：后端那份建表时写进库，
 *    是数据；这份只在"读不到数据"时顶一下，是保险绳。界面会明确标出
 *    「当前用内置清单」。两份的 method 都来自同一个源（`annot.ts`），不各写一套。
 */
export const FALLBACK_VALUES: Record<OptionKind, string[]> = {
  method: METHOD_OPTS,
  freq: ['全检', '抽检', '首件'],
  customer: [],
  project_type: [],
  mold_type: [],
  cavity_count: [],
  press_tonnage: [],
  // ★ 5 个预置值从 `lib/sipKind.ts` 派生（那同时也是中文标签表的键）
  sip_kind: SIP_KIND_PRESET,
};

interface OptionsState {
  /** 是否已经成功拉过一次 */
  ready: boolean;
  loading: boolean;
  /** 上次失败的原因（空 = 没失败） */
  error: string;
  /** 全量（含停用）—— 设置页用 */
  items: Record<string, OptionItem[]>;
  /** 启用中的值 —— 各处下拉用（已含出厂兜底） */
  values: Record<string, string[]>;
  /** 类名中文（后端下发，前端不另维护一份） */
  kindCn: Record<string, string>;
  /** true = 现在展示的是出厂兜底，不是后台清单 */
  degraded: boolean;

  load: (force?: boolean) => Promise<void>;
  /** 用一份完整响应重置 store（设置页改完后的**权威刷新**）。
   *
   *  ⚠️ 必须走这里，**不许在页面里自己 mergeValues** —— 那正是本项目头号坑
   *     （同一语义几份实现）：页面那份漏了兜底或漏了 degraded，就会与启动加载
   *     得出不一样的 values，表现为"设置页显示有、下拉里没有"。 */
  applyResponse: (r: OptionsResponse) => void;
  /** 设置页写完一项后**就地**把结果并回来，不必整表重拉 */
  applyItem: (item: OptionItem) => void;
  removeItem: (kind: string, id: number) => void;
}

/** 把后端 `values` 与出厂兜底合并：库里某类为空 ⇒ 用出厂值顶上。 */
function mergeValues(raw: Record<string, string[]> | undefined) {
  const out: Record<string, string[]> = {};
  for (const k of OPTION_KINDS) {
    const v = (raw?.[k] ?? []).filter((s) => String(s ?? '').trim() !== '');
    out[k] = v.length ? v : (FALLBACK_VALUES[k] ?? []);
  }
  // 后端将来加新 kind 时不被前端吞掉（前端不知道名字也照样给值）
  for (const [k, v] of Object.entries(raw ?? {})) {
    if (!(k in out) && Array.isArray(v) && v.length) out[k] = v;
  }
  return out;
}

export const useOptionsStore = create<OptionsState>((set, get) => ({
  ready: false,
  loading: false,
  error: '',
  items: {},
  values: mergeValues(undefined),
  kindCn: {},
  degraded: true,

  load: async (force = false) => {
    if (get().loading) return;
    if (get().ready && !force) return;
    set({ loading: true });
    try {
      const r = await getOptions();
      get().applyResponse(r);
      set({ loading: false, error: '' });
    } catch (e: any) {
      // ★ 失败**不清空已有值**（可能已经拿到过一份），只记错误 + 保持兜底可用
      set({
        loading: false, ready: true,
        error: e?.message || '读取后台清单失败',
        values: get().ready ? get().values : mergeValues(undefined),
        degraded: true,
      });
    }
  },

  applyResponse: (r) => {
    // 「降级」的判据是**后端有没有真的给出某类的非空值**，而不是请求成没成功：
    // 库已建但用户把某类清空时，后端 `resolve_all` 会回落给出厂值 —— 那是
    // 正常状态，不该一直挂着"降级"横幅吓人。
    const degraded = OPTION_KINDS.some(
      (k) => !(r.values?.[k] ?? []).some((s) => String(s ?? '').trim() !== ''));
    set({
      ready: true, loading: false, error: '',
      items: r.items ?? {},
      values: mergeValues(r.values),
      kindCn: r.kind_cn ?? {},
      degraded,
    });
  },

  applyItem: (item) => set((st) => {
    const cur = [...(st.items[item.kind] ?? [])];
    const i = cur.findIndex((x) => x.id === item.id);
    if (i >= 0) cur[i] = item; else cur.push(item);
    cur.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    // 值列表跟着重算：启用项按 sort_order
    const vals = cur.filter((x) => x.enabled).map((x) => x.value);
    return {
      items: { ...st.items, [item.kind]: cur },
      values: {
        ...st.values,
        [item.kind]: vals.length ? vals : (FALLBACK_VALUES[item.kind as OptionKind] ?? []),
      },
    };
  }),

  removeItem: (kind, id) => set((st) => {
    const cur = (st.items[kind] ?? []).filter((x) => x.id !== id);
    const vals = cur.filter((x) => x.enabled).map((x) => x.value);
    return {
      items: { ...st.items, [kind]: cur },
      values: {
        ...st.values,
        [kind]: vals.length ? vals : (FALLBACK_VALUES[kind as OptionKind] ?? []),
      },
    };
  }),
}));

/** 取某一类**可选值**（已含兜底）。组件里直接用这个。 */
export function useOptionValues(kind: OptionKind): string[] {
  return useOptionsStore((s) => s.values[kind] ?? FALLBACK_VALUES[kind] ?? []);
}
